import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import type { Archive } from '../api/archives';
import { PreviewEngine } from '../engine/preview';
import { totalDuration } from '../format/helpers';
import Transport from './Transport';
import { usePlayback } from '../state/playback';
import { useBufferingIndicator } from './useBufferingIndicator';
import { useTimeline } from '../state/timeline';
import type { PlayerControls } from './EditorPage';
import './PreviewPlayer.css';

interface Props {
  archives: Archive[];
  engineRef: MutableRefObject<PreviewEngine | null>;
  controls: PlayerControls;
}

export default function PreviewPlayer({ archives, engineRef, controls }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const currentTime = usePlayback((s) => s.currentTime);
  const playing = usePlayback((s) => s.playing);
  const buffering = useBufferingIndicator(usePlayback((s) => s.bufferingSources.length > 0));
  const edit = useTimeline((s) => s.edit);
  const duration = totalDuration(edit);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const engine = new PreviewEngine(canvas, () => useTimeline.getState().edit, archives);
    engine.onTime = (t) => usePlayback.getState().setTime(t);
    engine.onEnded = () => usePlayback.getState().setPlaying(false);
    engine.onBuffering = (ids) => usePlayback.getState().setBuffering(ids);
    engine.getLoop = () => {
      const l = usePlayback.getState().loop;
      return l && l.in !== null && l.out !== null && l.out > l.in
        ? { in: l.in, out: l.out }
        : null;
    };
    engineRef.current = engine;
    const unsub = useTimeline.subscribe((s, prev) => {
      if (s.edit !== prev.edit) engine.editChanged();
    });
    return () => {
      unsub();
      engine.dispose();
      usePlayback.getState().setBuffering([]);
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archives]);

  // low-res sources note
  const qualityNote = useMemo(() => {
    let maxRes = Infinity;
    let any = false;
    for (const src of Object.values(edit.sources)) {
      if (src.kind !== 'pandora-video') continue;
      any = true;
      const archive = archives.find((a) => a.key === src.archive);
      if (archive) maxRes = Math.min(maxRes, Math.max(...archive.resolutions));
    }
    if (any && isFinite(maxRes) && maxRes < edit.settings.height)
      return `source streams are ${maxRes}p — below the ${edit.settings.height}p project`;
    return null;
  }, [edit.sources, edit.settings.height, archives]);

  return (
    <div className="preview">
      <div className="preview-stage">
        <canvas
          ref={canvasRef}
          className="preview-canvas"
          style={{ aspectRatio: `${edit.settings.width} / ${edit.settings.height}` }}
          onClick={() => controls.toggle()}
        />
        {buffering && (
          <div className="preview-buffering" aria-live="polite">
            <span className="preview-buffering-dot" />
            loading video — showing preview frame
          </div>
        )}
      </div>
      {qualityNote && <div className="preview-quality">{qualityNote}</div>}
      <Transport
        playing={playing}
        currentTime={currentTime}
        duration={duration}
        fps={edit.settings.fps}
        onToggle={() => controls.toggle()}
        onSeek={(t) => controls.seek(t)}
        onStep={(dir) => controls.stepFrame(dir)}
        className="preview-transport"
        playClassName="preview-play"
        timecodeClassName="preview-timecode"
      />
    </div>
  );
}
